// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId";
import { useBasicPaginatedResource } from "@Common/hooks/Pagination/useBasicPaginatedResource.tsx";

// Utils
import { and } from "@Common/helpers/sonar";
import { getAllDeals } from "@DataPages/api/financeManagement/allDeals/queries";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IPageParam } from "@/Common/api/types/pagination";

const PAGE_SIZE = 250;

export const useAllDealsPaginatedQuery = (selectedPayPeriod: IPeriodTreeSelection, canRequestAllDeals: boolean) => {
	const { cycleId: commissionCycleId = "", payPeriodId = "" } = selectedPayPeriod ?? {};

	const locationId = useLocationId();

	const { data, ...otherResults } = useBasicPaginatedResource({
		queryKey: ["allDeals", locationId, selectedPayPeriod?.cycleId, selectedPayPeriod?.payPeriodId],
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const allDeals = await getAllDeals({
				locationId,
				commissionCycleId,
				payPeriodId,
				pagination: {
					first: typedPageParam.first,
					after: typedPageParam.after,
				},
			});

			if (!allDeals) {
				return {
					items: [],
					pageInfo: {
						hasNextPage: false,
						endCursor: "",
					},
				};
			}

			return allDeals;
		},
		getNextPageParam: (lastPage) => {
			const { pageInfo } = lastPage ?? {};
			return pageInfo?.hasNextPage ? { first: PAGE_SIZE, after: pageInfo.endCursor ?? undefined } : undefined;
		},
		initialPageParam: {
			first: PAGE_SIZE,
			after: "",
		},
		fetchAllPages: true,
		enabled: and(Boolean(locationId), Boolean(selectedPayPeriod?.cycleId), Boolean(selectedPayPeriod?.payPeriodId), canRequestAllDeals),
		staleTime: Infinity,
	});

	return {
		data,
		...otherResults,
	};
};
